/**
 * Quick search - thin typed client over /api/crm/quick-search (docs/plans/crm-report-builder-search-collab-plan.md
 * B2.3; backend/src/routes/crmQuickSearch.js), in the layout of recruit/api.ts.
 *
 * The typed text travels in a POST body, never in a URL, so a name or a number never lands in the
 * request log or the browser history (B2.3); nothing here logs it. The recently viewed read sends
 * lead ids only. Each answer is read defensively (quickSearchLogic normalizeQuickResponse), so a
 * malformed row is dropped rather than rendered.
 */
import api from '../../services/api';
import {
  MIN_CHARS,
  clipQuery,
  emptyQuickResponse,
  normalizeLeads,
  normalizeQuickResponse,
  recentRequestIds,
} from './quickSearchLogic';
import type { QuickLead, QuickSearchRequest, QuickSearchResponse } from './types';

const BASE = '/api/crm/quick-search';

// ---------------------------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------------------------

/**
 * True for a request this client aborted itself (each keystroke aborts the one before, and closing
 * the palette aborts what is in flight): not an error, so nothing is shown for it.
 */
export function isQuickSearchAborted(e: unknown): boolean {
  const err = e as { code?: unknown; name?: unknown } | null | undefined;
  return !!err && (err.code === 'ERR_CANCELED' || err.name === 'CanceledError' || err.name === 'AbortError');
}

/** True when this api does not serve quick search (an older deploy) or is down. */
export function isQuickSearchUnavailable(e: unknown): boolean {
  if (isQuickSearchAborted(e)) return false;
  const status = (e as any)?.response?.status;
  return status === 404 || status === 502 || status === 503 || !(e as any)?.response;
}

/** The plain sentence to show for a failed search. Never a raw status line or stack. */
export function quickSearchErrorMessage(e: unknown, fallback = "Search isn't available right now. Please try again."): string {
  const res = (e as any)?.response;
  const data = res?.data;
  if (data && typeof data.error === 'string' && data.error.trim()) return data.error;
  if (res?.status === 403) return "You don't have access to that.";
  return fallback;
}

// ---------------------------------------------------------------------------------------------
// The two routes (B2.3)
// ---------------------------------------------------------------------------------------------

/**
 * POST /quick-search { q }: leads, studies and how the text was read. The text is trimmed and
 * clipped to 100 characters as the server does; below MIN_CHARS it answers the empty result without
 * asking (the server would answer the same). Pass the keystroke's AbortController signal.
 */
export async function quickSearch(q: string, signal?: AbortSignal): Promise<QuickSearchResponse> {
  const text = clipQuery(q);
  if (text.length < MIN_CHARS) return emptyQuickResponse();
  const body: QuickSearchRequest = { q: text };
  const { data } = await api.post(BASE, body, { signal });
  return normalizeQuickResponse(data);
}

/**
 * GET /quick-search/recent?ids=<csv>: the recently viewed leads, in the order asked, a merged one as
 * its kept record, a missing or other-network one left out. Only lead ids are sent (junk dropped,
 * each once, at most ten); none left answers [] without asking.
 */
export async function resolveRecent(ids: readonly string[], signal?: AbortSignal): Promise<QuickLead[]> {
  const clean = recentRequestIds(ids);
  if (!clean.length) return [];
  const { data } = await api.get(`${BASE}/recent`, { params: { ids: clean.join(',') }, signal });
  return normalizeLeads(data?.leads);
}
